import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useLang } from "../i18n";
import { scrollToId } from "../lenis";

const LOGO = "https://customer-assets-agu9un31.emergentagent.net/job_smart-locker-hub-5/artifacts/mk8y32ow_ChatGPT%20Image%203%20ago%202026%2C%2016_17_05.png";

export default function Header() {
  const { t, lang, toggle } = useLang();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/#" + id);
    } else {
      scrollToId(id);
    }
  };

  const goHome = () => {
    setOpen(false);
    if (location.pathname !== "/") navigate("/");
    else scrollToId("home");
  };

  const links = [
    { id: "home", label: t.nav.home },
    { id: "solutions", label: t.nav.solutions },
    { id: "products", label: t.nav.products },
    { id: "how", label: t.nav.how },
    { id: "sectors", label: t.nav.forWho },
    { id: "faq", label: t.nav.faq },
    { id: "contact", label: t.nav.contacts },
  ];

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 border-b transition-colors duration-300 ${
        scrolled ? "border-border bg-white/95 backdrop-blur-md" : "border-transparent bg-white"
      }`}
      data-testid="site-header"
    >
      <div className="mx-auto flex h-16 max-w-[1400px] items-center justify-between px-5 sm:h-20 sm:px-8">
        <button onClick={goHome} className="flex items-center" data-testid="logo-button" aria-label="Boxly home">
          <img src={LOGO} alt="Boxly" className="h-6 w-auto sm:h-7" />
        </button>

        <nav className="hidden items-center gap-8 lg:flex" aria-label="Primary" data-testid="desktop-nav">
          {links.map((l) => (
            <button
              key={l.id}
              onClick={() => go(l.id)}
              className="group relative font-grotesk text-sm font-medium text-ink/80 transition-colors hover:text-ink"
              data-testid={`nav-${l.id}`}
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-0.5 w-0 bg-electric-blue transition-all duration-300 group-hover:w-full" />
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={toggle}
            className="hidden items-center rounded-md border border-border px-3 py-1.5 font-mono text-xs font-semibold uppercase tracking-wider text-ink transition-colors hover:border-ink sm:flex"
            data-testid="lang-toggle"
            aria-label={lang === "it" ? "Switch to English" : "Passa all'italiano"}
          >
            {lang === "it" ? "IT / EN" : "EN / IT"}
          </button>

          <button
            onClick={() => go("contact")}
            className="group hidden items-center gap-1.5 rounded-md bg-electric-blue px-5 py-2.5 font-grotesk text-sm font-bold text-white transition-colors duration-300 hover:bg-ink hover:text-lime-accent sm:flex"
            data-testid="header-cta"
          >
            {t.nav.cta}
            <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>

          <button
            onClick={() => setOpen((v) => !v)}
            className="flex h-10 w-10 items-center justify-center rounded-md border border-border lg:hidden"
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
            aria-expanded={open}
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden border-t border-border bg-white lg:hidden"
            data-testid="mobile-nav"
          >
            <div className="flex flex-col px-5 py-4">
              {links.map((l) => (
                <button
                  key={l.id}
                  onClick={() => go(l.id)}
                  className="border-b border-border/60 py-3 text-left font-grotesk text-base font-medium text-ink"
                  data-testid={`mobile-nav-${l.id}`}
                >
                  {l.label}
                </button>
              ))}
              <div className="mt-4 flex items-center gap-3">
                <button onClick={toggle} className="rounded-md border border-border px-4 py-2 font-mono text-xs font-semibold uppercase" data-testid="mobile-lang-toggle">
                  {lang === "it" ? "IT / EN" : "EN / IT"}
                </button>
                <button onClick={() => go("contact")} className="flex-1 rounded-md bg-electric-blue px-4 py-2.5 font-grotesk text-sm font-bold text-white" data-testid="mobile-cta">
                  {t.nav.cta}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
